import {
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { Encounter, EncounterStatus, WithId } from '@hb/shared'
import { useCallback, useState } from 'react'
import { requestExamPdf, requestPrenatalRecordPdf } from '../../../../backend/functions/other'
import { downloadFromStorage } from '../../../../backend/storage/utils'
import { useGynCareView } from '../../../../components/Gyn/context'
import { useProfile } from '../../../../contexts'

interface Props {
  encounters: WithId<Encounter>[]
  assessmentId: string
  isOpen: boolean
  onClose: () => void
}

export const PdfExportModal = ({ encounters, assessmentId, isOpen, onClose }: Props) => {
  const { practiceId } = useGynCareView()
  const { userId: patientId } = useProfile()
  const toast = useToast()
  const [loadingSingle, setLoadingSingle] = useState(false)
  const [loadingFull, setLoadingFull] = useState(false)

  const authenticatedEncounters = encounters.filter(e => e.status === EncounterStatus.AUTHENTICATED)
  const mostRecentAuthenticated = authenticatedEncounters[0] ?? null

  const handleExportSingle = useCallback(async () => {
    if (!mostRecentAuthenticated) {
      toast({
        title: 'PDF requires an authenticated visit note',
        description: 'Authenticate the most recent visit before exporting a PDF.',
        status: 'warning',
        isClosable: true,
      })
      return
    }
    if (!practiceId || !patientId) return
    setLoadingSingle(true)
    try {
      const { data } = await requestExamPdf({
        practiceId,
        patientId,
        examIds: [mostRecentAuthenticated.id],
      })
      await downloadFromStorage(data.storagePath)
    } catch (err) {
      console.error('Failed to generate exam PDF', err)
      toast({ title: 'Failed to generate PDF', status: 'error', isClosable: true })
    } finally {
      setLoadingSingle(false)
    }
  }, [mostRecentAuthenticated, patientId, practiceId, toast])

  const handleExportFull = useCallback(async () => {
    if (!practiceId || !patientId || !assessmentId) return
    setLoadingFull(true)
    try {
      const { data } = await requestPrenatalRecordPdf({ practiceId, patientId, assessmentId })
      await downloadFromStorage(data.storagePath)
    } catch (err) {
      console.error('Failed to generate prenatal record PDF', err)
      toast({ title: 'Failed to generate prenatal record PDF', status: 'error', isClosable: true })
    } finally {
      setLoadingFull(false)
    }
  }, [assessmentId, patientId, practiceId, toast])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent borderRadius={8}>
        <ModalHeader
          fontFamily="Hero-New"
          bg="gray.50"
          borderTopRadius={8}
          borderBottom="1px solid #cdcdcd"
          py={2}
          px={4}
          fontSize="md">
          Export / Print
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={5}>
          <VStack spacing={4} align="stretch">
            <Button
              variant="outline"
              colorScheme="blue"
              fontFamily="Hero-New"
              onClick={handleExportSingle}
              isLoading={loadingSingle}
              isDisabled={!mostRecentAuthenticated}>
              Export this visit (most recent)
            </Button>
            {!mostRecentAuthenticated && (
              <Text fontSize="xs" color="orange.500" fontFamily="Hero-New" textAlign="center">
                No authenticated visits — authenticate a note first.
              </Text>
            )}
            <Divider />
            <Button
              variant="outline"
              colorScheme="green"
              fontFamily="Hero-New"
              onClick={handleExportFull}
              isLoading={loadingFull}
              isDisabled={!assessmentId}>
              Export full prenatal record
            </Button>
            <Text fontSize="xs" color="gray.400" fontFamily="Hero-New" textAlign="center">
              Flow sheet summary + all visit notes + contact log, oldest to newest.
            </Text>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
